import { useContext } from 'react'
import { appContext } from './AppContext';
import { dateToAge, getDateAge, dateToAgeMonths, ageToMonths } from './dateUtil';
import { formatNumber, getPersonIdValidation } from './inputUtil';
import { v4 as uuidv4 } from 'uuid';
import { initialKid } from './RegisrationContext'
import { FAMILY_MIN_LOAN, familyTierPlans } from './familyPlanUtil'
import { getAvailablePlans, planFitsAge, newPlanRow, rowMaxUnits } from './planUtils';

export default function useUpdateKidCB({ donor = {}, kids, setKids, planMode = 'standard', familyPlans = [], familyChildrenCount = null }) {

    const isFamily = planMode === 'family';

    // Keep a family plan only while its tier still fits the family (children count + oldest age)
    const familyPlanForAge = (kid, age) => {
        const children = (kid?.preBorn) ? 0 : familyChildrenCount;
        const tier = familyTierPlans(familyPlans, children, age);
        return (tier.some(plan => plan.route === kid?.plan?.route)) ? kid.plan : null;
    }

    const { plans } = useContext(appContext);

    //handle add kid
    const addKid = () => {
        setKids(prev => [...prev, isFamily ? { ...initialKid, id: uuidv4() } : { ...initialKid, plans: [newPlanRow()], id: uuidv4() }])
    };

    //Prune rows that no longer fit the age (keep requestLine for SF line update), auto-select when exactly one plan is available
    const applyAgeChange = (kid, ageMonths) => {
        const available = getAvailablePlans(plans, ageMonths);
        //clear rows whose plan no longer fits the new age
        let rows = (kid?.plans ?? [newPlanRow()]).map(row =>
            (row.plan && !planFitsAge(row.plan, ageMonths)) ? { ...row, plan: null, units: 0 } : row);
        //collapse multiple empty rows into a single one
        const selectedRows = rows.filter(row => row.plan);
        const emptyRows = rows.filter(row => !row.plan);
        rows = (selectedRows.length) ? [...selectedRows, ...emptyRows.slice(0, 1)] : [emptyRows[0] ?? newPlanRow()];
        //auto-select when exactly one plan is available and no plan remains selected
        let autoSelected = false;
        if (available.length === 1 && !selectedRows.length) {
            const maxUnits = (available[0]?.units) ? available[0].units : 0;
            rows = [{ ...rows[0], plan: available[0], units: (rows[0].units && maxUnits && rows[0].units > maxUnits) ? maxUnits : rows[0].units }];
            autoSelected = true;
        }
        return {
            rows,
            signal: {
                availableCount: available.length,
                autoSelected,
                openPopup: available.length > 1 && !selectedRows.length,
                openPopupRowId: rows.find(row => !row.plan)?.rowId ?? null,
            }
        };
    }

    //update kid fields via CallBack fn
    const updateKidCB = (id, key, rowId = null) => {

        // Generate kid CallBack fn
        return function callback(value) {
            const currentKid = kids?.find(kid => kid.id === id);
            // case field age
            if (key === 'age') {
                // Family mode: no auto plan / no preBorn conversion (sub-mode governs preBorn)
                if (isFamily) {
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, age: value } : kid))
                    return
                }
                // Standard multi-plan: re-evaluate available plan rows for the new age
                if (Number(value) === 0) {
                    //preBorn kid - age 0 months
                    const { rows, signal } = applyAgeChange(currentKid, 0);
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, preBorn: true, birthday: '', plans: rows } : kid))
                    return signal
                }
                const { rows, signal } = applyAgeChange(currentKid, ageToMonths(value));
                setKids(prev => prev.map(kid => (kid.id === id) ? {
                    ...kid,
                    [key]: value,
                    preBorn: (Number(value) > 3) ? false : kid?.preBorn,
                    birthday: (kid?.birthday) ? getDateAge(kid?.birthday, value) : '',
                    plans: rows,
                } : kid))
                return signal
            }

            // case field loanAmount (family plan slider)
            if (key === 'loanAmount') {
                setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, loanAmount: Number(value) } : kid))
                return
            }

            // case field plan (family mode only - standard mode uses 'rowPlan')
            if (key === 'plan') {
                // Family mode: pick the grouped family plan (premium / inflation) and default the loan
                let selectedPlan = familyPlans?.find(plan => plan?.route === Number(value))
                setKids(prev => prev.map(kid => (kid.id === id) ? {
                    ...kid,
                    plan: selectedPlan,
                    loanAmount: kid?.loanAmount || FAMILY_MIN_LOAN,
                } : kid))
                return
            }

            // case plan selected for a specific row (standard multi-plan)
            if (key === 'rowPlan') {
                let selectedPlan = plans?.find(plan => plan?.route === Number(value))
                setKids(prev => prev.map(kid => (kid.id === id) ? {
                    ...kid,
                    plans: kid.plans.map(row => {
                        if (row.rowId !== rowId) return row
                        const maxUnits = rowMaxUnits(kid, rowId, selectedPlan);
                        return { ...row, plan: selectedPlan, units: (row.units && row.units > maxUnits) ? maxUnits : row.units }
                    })
                } : kid))
                return
            }

            // case units changed for a specific row (standard multi-plan)
            if (key === 'rowUnits') {
                setKids(prev => prev.map(kid => (kid.id === id) ? {
                    ...kid,
                    plans: kid.plans.map(row => {
                        if (row.rowId !== rowId) return row
                        const maxUnits = rowMaxUnits(kid, rowId, row.plan);
                        return { ...row, units: Math.min(Number(value) || 0, maxUnits) }
                    })
                } : kid))
                return
            }

            if (key === 'preBorn') {
                // Family mode: keep single-plan shape (sub-mode governs preBorn, clear plan on toggle)
                if (isFamily) {
                    if (value)
                        setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, name: '', age: 0, birthday: '', plan: null } : kid))
                    else
                        setKids(prev => prev.map(kid => (kid.id === id) ? (kid.birthday) ? { ...kid, [key]: value, age: dateToAge(kid.birthday), plan: null } : { ...kid, [key]: value, age: null, plan: null } : kid))
                    return
                }
                // Standard multi-plan - preBorn child has no name/ID
                if (value) {
                    const { rows, signal } = applyAgeChange(currentKid, 0);
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, name: '', age: 0, birthday: '', plans: rows, personId: '', personIdValidation: { invalid: false, errorText: '' } } : kid))
                    return signal
                }
                const age = (currentKid?.birthday) ? dateToAge(currentKid.birthday) : null;
                const ageMonths = (currentKid?.birthday) ? dateToAgeMonths(currentKid.birthday) : ageToMonths(age);
                const { rows, signal } = applyAgeChange(currentKid, ageMonths);
                setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, age, plans: rows } : kid))
                return signal
            }

            if (key === 'birthday') {
                let age = dateToAge(value)
                const ageMonths = dateToAgeMonths(value)
                // While typing the year digit-by-digit the input briefly emits an incomplete date with
                // a leading-zero year (e.g. "0002-05-01"), which isValidDate rejects -> dateToAgeMonths
                // is null. Store the raw value but don't re-evaluate age/plans, so the selected plans
                // aren't wiped mid-typing. Once the full year is typed, ageMonths is real again.
                if (ageMonths === null) {
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value } : kid))
                    return
                }
                // Family mode (oldest child): set birthday/age; reset the plan when no tier fits the new age
                if (isFamily) {
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, age, plan: familyPlanForAge(kid, age) } : kid))
                    return
                }
                // Standard multi-plan: re-evaluate plan rows from precise birthday months
                const { rows, signal } = applyAgeChange(currentKid, ageMonths);
                if (age === 0)
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, name: '', age, preBorn: true, birthday: '', plans: rows, personId: '', personIdValidation: { invalid: false, errorText: '' } } : kid))
                else
                    setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, age, plans: rows } : kid))
                return signal
            }

            if (key === 'personId') {
                value = formatNumber(value);
                // Optional ת.ז (like the lead page): empty is valid; validate only when filled.
                let personIdValidation = (value.length === 0) ? { invalid: false, errorText: '' } : getPersonIdValidation(value)
                if (!personIdValidation.invalid && value.length > 0 && (donor?.personId === value || kids?.some(kid => kid.personId === value))) {
                    personIdValidation = { invalid: true, errorText: 'ת.ז כבר בשימוש' }
                }
                setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value, personIdValidation } : kid))
            }
            //update Key:value
            else
                setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, [key]: value } : kid))
        }
    }

    //handle add plan row to kid (standard multi-plan)
    const addPlanRow = (id) => {
        const rowId = uuidv4();
        setKids(prev => prev.map(kid => (kid.id === id) ? { ...kid, plans: [...kid.plans, { rowId, plan: null, units: 0 }] } : kid))
        return rowId
    }

    //handle remove plan row from kid (never below one row)
    const removePlanRow = (id, rowId) => {
        setKids(prev => prev.map(kid => {
            if (kid.id !== id) return kid
            const rows = kid.plans.filter(row => row.rowId !== rowId)
            return { ...kid, plans: (rows.length) ? rows : [newPlanRow()] }
        }))
    }

    //handle remove kid
    const removeKid = (id) => {
        setKids(prev => prev.filter(kid => (kid.id !== id)))
    }



    return { addKid, updateKidCB, removeKid, addPlanRow, removePlanRow }

}
